import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/sonner";

const EMPTY = { name: "", description: "", duration_min: 30, buffer_before_min: 0, buffer_after_min: 0, price: "", active: true, assignment: "any", employee_ids: [] };

export default function Services() {
  const [services, setServices] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    API.get("/services").then((r) => setServices(r.data)).catch((e) => toast.error(formatApiError(e)));
    API.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openDialog = (service) => {
    if (service) {
      setEditing(service);
      setForm({ ...service, price: service.price ?? "" });
    } else {
      setEditing(null);
      setForm(EMPTY);
    }
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    const payload = { ...form, duration_min: Number(form.duration_min), buffer_before_min: Number(form.buffer_before_min), buffer_after_min: Number(form.buffer_after_min), price: form.price === "" ? null : Number(form.price) };
    try {
      if (editing) await API.put(`/services/${editing.id}`, payload);
      else await API.post("/services", payload);
      toast.success(editing ? "Service modifié" : "Service créé");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    try {
      await API.delete(`/services/${s.id}`);
      toast.success("Service supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="services-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold text-slate-900">Services</h1>
        <Button onClick={() => openDialog(null)} data-testid="btn-new-service"><Plus className="mr-2 h-4 w-4" /> Nouveau service</Button>
      </div>

      {services === null ? (
        <p className="text-sm text-slate-400">Chargement...</p>
      ) : services.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="services-empty">
          <p className="text-sm text-slate-500">Aucun service. Créez votre premier service pour activer la réservation.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s) => (
            <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`service-card-${s.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-display text-lg font-semibold text-slate-900">{s.name}</h3>
                  <p className="mt-0.5 text-xs text-slate-500 line-clamp-2">{s.description}</p>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${s.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  {s.active ? "Actif" : "Inactif"}
                </span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2 text-xs text-slate-600">
                <span className="rounded-md bg-slate-100 px-2 py-1">{s.duration_min} min</span>
                {s.price != null && <span className="rounded-md bg-slate-100 px-2 py-1">{s.price} €</span>}
                {(s.buffer_before_min > 0 || s.buffer_after_min > 0) && (
                  <span className="rounded-md bg-slate-100 px-2 py-1">Tampon {s.buffer_before_min}/{s.buffer_after_min} min</span>
                )}
                <span className="rounded-md bg-slate-100 px-2 py-1">
                  {s.assignment === "any" ? "Premier disponible" : `${s.employee_ids.length} employé(s)`}
                </span>
              </div>
              <div className="mt-4 flex gap-2">
                <Button variant="outline" size="sm" onClick={() => openDialog(s)} data-testid={`btn-edit-service-${s.id}`}>
                  <Pencil className="mr-1 h-3 w-3" /> Modifier
                </Button>
                <Button variant="ghost" size="sm" className="text-red-600" onClick={() => remove(s)} data-testid={`btn-delete-service-${s.id}`}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="service-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Modifier le service" : "Nouveau service"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="service-form-name" className="mt-1" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="service-form-description" className="mt-1" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Durée (min)</Label>
                <Input type="number" min={5} value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} data-testid="service-form-duration" className="mt-1" />
              </div>
              <div>
                <Label>Tampon avant</Label>
                <Input type="number" min={0} value={form.buffer_before_min} onChange={(e) => setForm({ ...form, buffer_before_min: e.target.value })} data-testid="service-form-buffer-before" className="mt-1" />
              </div>
              <div>
                <Label>Tampon après</Label>
                <Input type="number" min={0} value={form.buffer_after_min} onChange={(e) => setForm({ ...form, buffer_after_min: e.target.value })} data-testid="service-form-buffer-after" className="mt-1" />
              </div>
            </div>
            <div>
              <Label>Prix (€, facultatif)</Label>
              <Input type="number" min={0} step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="service-form-price" className="mt-1" />
            </div>
            <div>
              <Label>Attribution</Label>
              <Select value={form.assignment} onValueChange={(v) => setForm({ ...form, assignment: v })}>
                <SelectTrigger data-testid="service-form-assignment" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Premier employé disponible</SelectItem>
                  <SelectItem value="specific">Employés spécifiques</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.assignment === "specific" && (
              <div className="space-y-2 rounded-lg border border-slate-200 p-3" data-testid="service-form-employees">
                {employees.map((emp) => (
                  <label key={emp.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form.employee_ids.includes(emp.id)}
                      onCheckedChange={(checked) =>
                        setForm({ ...form, employee_ids: checked ? [...form.employee_ids, emp.id] : form.employee_ids.filter((x) => x !== emp.id) })
                      }
                      data-testid={`service-emp-${emp.id}`}
                    />
                    {emp.name}
                  </label>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <Label>Service actif</Label>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="service-form-active" />
            </div>
            <Button className="w-full" onClick={save} disabled={!form.name || saving} data-testid="service-form-submit">
              {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
